import { useCallback, useEffect, useRef, useState } from 'react';

import ComponentsPrintView from '../components/components-print-view';
import {
  ActivityBuilderIntroFooter,
  ActivityBuilderIntroHeader,
} from '../introduction/activity-builder-intro';
import { ActivityBuilderSlideContent } from '../activity-builder-slide.style';
import { Orientation } from '../activity-builder.apollo';
import * as Styled from './activity-builder-a4.style';
import ActivityPrint from '../components/activity/activity-print';
import {
  ACTIVITY_BUILDER_A4_SHORT_SIDE,
  ACTIVITY_BUILDER_ACTIVITY_HEIGHT,
  ACTIVITY_BUILDER_FOOTER_HEIGHT,
  ACTIVITY_BUILDER_HEADER_HEIGHT,
  SectionOrSubSection,
} from '../activity-builder.constants';
import type { InteractiveLessonSectionOverviewFragment } from '../lesson-builder.graphql.generated';
import {
  ACTIVITIES_COMPONENT_TYPE_DRAW,
  LANGUAGE_CODE_EN_AU,
  LanguageCodes,
} from 'global/constants';
import { ComponentDraw } from '../components/component.constants';
import { getComponentsOfType } from '../components/shared/logic/get-components-of-type';

type Props = {
  index: number;
  section: SectionOrSubSection;
  slidesPerPage: number;
  orientation: Orientation;
  includesIntro: boolean;
  version: string | undefined;
  unitName: string;
  contentId: number;
  contentName: string;
  subject: string;
  sectionsOverview: InteractiveLessonSectionOverviewFragment[];
  language: LanguageCodes;
  onRenderComplete?: () => void;
};

export const SLIDE_HEIGHT_MINUS_HEADER_FOOTER =
  ACTIVITY_BUILDER_A4_SHORT_SIDE -
  ACTIVITY_BUILDER_HEADER_HEIGHT -
  ACTIVITY_BUILDER_FOOTER_HEIGHT -
  ACTIVITY_BUILDER_ACTIVITY_HEIGHT -
  32; // top padding between activity and page content

const ActivityBuilderPrintGeneratorPage = ({
  index,
  section,
  slidesPerPage,
  orientation,
  includesIntro,
  version,
  unitName,
  contentName,
  subject,
  contentId,
  sectionsOverview,
  language,
  onRenderComplete,
}: Props) => {
  const pageRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState<number | null>(null);

  const totalResponseComponents = useRef<number>(
    getComponentsOfType<ComponentDraw>({
      components: section.components,
      types: [ACTIVITIES_COMPONENT_TYPE_DRAW],
    }).length
  );
  const completedResponseComponents = useRef<number>(0);

  // The parent counts one onRenderComplete per page to know when every page
  // is ready to print — so this page must call it exactly once, regardless
  // of how many draw components it contains or how many times they report.
  const handleResponseComponentRenderComplete = useCallback(() => {
    completedResponseComponents.current += 1;
    if (
      completedResponseComponents.current === totalResponseComponents.current
    ) {
      onRenderComplete?.();
    }
  }, [onRenderComplete]);

  useEffect(() => {
    const handleResize = () => {
      if (pageRef.current) {
        const pageHeight = pageRef.current.offsetHeight;
        if (pageHeight > SLIDE_HEIGHT_MINUS_HEADER_FOOTER) {
          const scaleFactor = SLIDE_HEIGHT_MINUS_HEADER_FOOTER / pageHeight;
          setScale(scaleFactor);
        } else {
          setScale(1);
        }
      }
    };
    handleResize();
  }, []);

  useEffect(() => {
    if (scale !== null && totalResponseComponents.current === 0) {
      onRenderComplete?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale]);

  return (
    <Styled.A4Slide
      $twoPerPage={slidesPerPage === 2}
      $orientation={orientation}
      key={section.id}
    >
      <ActivityBuilderIntroHeader
        unitName={unitName}
        contentName={contentName}
      />
      <ActivityBuilderSlideContent>
        <ActivityPrint section={section} sectionsOverview={sectionsOverview} />
        <div
          ref={pageRef}
          style={
            scale
              ? { transform: `scale(${scale})`, transformOrigin: 'top left' }
              : undefined
          }
        >
          <ComponentsPrintView
            section={section}
            contentId={contentId}
            isPrintPreview={false}
            contentVersion={version}
            language={language}
            onRenderComplete={handleResponseComponentRenderComplete}
          />
        </div>
      </ActivityBuilderSlideContent>
      <ActivityBuilderIntroFooter
        pageNumber={index + (includesIntro ? 2 : 1)}
        subject={subject}
        showGoogleAttribution={language !== LANGUAGE_CODE_EN_AU}
      />
    </Styled.A4Slide>
  );
};

export default ActivityBuilderPrintGeneratorPage;
